import { Slide } from '@mui/material';
import { SurveyQuestionType } from 'posthog-js';
import { usePostHog } from 'posthog-js/react';
import type { Survey } from 'posthog-js';

import { Icon, icons } from '../../generic';
import { RatingQuestion } from '../../generic/Survey/questions/RatingQuestion';
import { defaultQuestionRenderers } from '../../generic/Survey/questions/registry';
import { surveyEventProperties } from '../../generic/Survey/surveyEventProperties';
import { SurveyWizard } from '../../generic/Survey/SurveyWizard';
import type { QuestionRenderer, QuestionRendererProps } from '../../generic/Survey/questions/registry';

import styles from './SurveyToast.module.less';

// Override the default rating renderer to suppress the lowerBoundLabel /
// upperBoundLabel beneath the scale. The corner popup is too narrow for those
// labels to read comfortably; the rating buttons + question text carry enough
// meaning on their own. Module-level constant so the renderer map reference is
// stable across re-renders.
const compactRatingQuestion: QuestionRenderer = ({
  question, value, onChange,
}: QuestionRendererProps) => (
  <RatingQuestion
    question={question}
    value={value}
    onChange={onChange}
    shouldShowRangeLabels={false}
  />
);

const compactQuestionRenderers = {
  ...defaultQuestionRenderers,
  [SurveyQuestionType.Rating]: compactRatingQuestion,
};

type Props = {
  id: number;
  isVisible: boolean;
  survey: Survey;
  onClose: (id: number) => void;
  onSurveySubmit?: () => void;
};

/**
 * Toast variant hosting a PostHog survey. Renders the survey body via
 * `SurveyWizard` inside a white card with an X close button. Slides in/out
 * from the right matching other toasts. Dismissing fires
 * `posthog.capture('survey dismissed', { $survey_id })`.
 */
export const SurveyToast = ({ id, isVisible, survey, onClose, onSurveySubmit }: Props) => {
  const posthog = usePostHog();

  const handleDismiss = () => {
    posthog?.capture('survey dismissed', surveyEventProperties(survey.id, survey.current_iteration));
    onClose(id);
  };

  return (
    <Slide in={isVisible} mountOnEnter unmountOnExit direction="left">
      <div
        className={`fossa-toast ${styles.surveyToast}`}
        role="dialog"
        aria-label={survey.name}
      >
        <button
          type="button"
          aria-label="Dismiss survey"
          className={styles.dismissButton}
          onClick={handleDismiss}
        >
          <Icon icon={icons.times} />
        </button>
        <SurveyWizard
          survey={survey}
          onSubmit={onSurveySubmit}
          variant="compact"
          questionRenderers={compactQuestionRenderers}
        />
      </div>
    </Slide>
  );
};
